import { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router';
import toast from 'react-hot-toast';
import { useTheme } from '@emotion/react';
import Chart from 'react-apexcharts';
import {
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  Stack,
  Switch,
  TextField,
  Typography,
  Skeleton,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';

import { useDispatch } from 'store';
import {
  createLiveForecastConfig,
  getForeCastGraph,
  getLiveForecastConfig,
} from 'store/slices/evalmetrics';
import PrimaryButton from 'ui-component/button/PrimaryButton';
import AnimateButton from 'ui-component/extended/AnimateButton';

// Constants
const DEFAULT_LIMIT = 1000;
const DEFAULT_SKIP = 0;

const CHART_HEIGHT = 350;

const INITIAL_CONFIG = {
  y_axis: '',
  index1: '',
  index2: '',
  index3: '',
  index1_status: true,
  index2_status: true,
  index3_status: true,
};

const CONFIG_TEXT_FIELDS = [
  { key: 'y_axis', label: 'Y-axis' },
  { key: 'index1', label: 'Index 1' },
  { key: 'index2', label: 'Index 2' },
  { key: 'index3', label: 'Index 3' },
];

const STATUS_TOGGLE_FIELDS = [
  { key: 'index1_status', label: 'Index 1 Status' },
  { key: 'index2_status', label: 'Index 2 Status' },
  { key: 'index3_status', label: 'Index 3 Status' },
];

const parseStatusValue = (value) => {
  if (typeof value === 'boolean') return value;
  if (value === 'true' || value === '1' || value === 1) return true;
  return false;
};

const getForecastTime = (row) => {
  const time = Date.parse(row?.date || row?.['Unnamed: 0'] || row?.timestamp);
  return Number.isNaN(time) ? 0 : time;
};

const normalizeListResponse = (data) => {
  const list = Array.isArray(data)
    ? data
    : Array.isArray(data?.data)
      ? data.data
      : [];
  return [...list].sort((a, b) => getForecastTime(a) - getForecastTime(b));
};

const normalizeConfig = (data = {}) => {
  const normalized = { ...data };

  ['index1_status', 'index2_status', 'index3_status'].forEach((key) => {
    if (data[key] !== undefined && data[key] !== null) {
      normalized[key] = parseStatusValue(data[key]);
    }
  });

  return normalized;
};

const CHART_COLORS = ['#00BAEC', '#77B6EA', '#545454'];

const FORECAST_SERIES = [
  {
    configKey: 'index1',
    valKey: 'val_1',
    statusKey: 'index1_status',
    color: CHART_COLORS[0],
    defaultLabel: 'MQE Strategy',
  },
  {
    configKey: 'index2',
    valKey: 'val_2',
    statusKey: 'index2_status',
    color: CHART_COLORS[1],
    defaultLabel: 'S&P 500',
  },
  {
    configKey: 'index3',
    valKey: 'val_3',
    statusKey: 'index3_status',
    color: CHART_COLORS[2],
    defaultLabel: 'HFRI 500',
  },
];

const resolveSeriesLabel = (configLabel, defaultLabel) => {
  const configText = String(configLabel || '').trim();
  if (configText) return configText;

  return defaultLabel;
};

const getSeriesLabel = (series, config) => {
  const configLabel = config[series.configKey];

  return resolveSeriesLabel(configLabel, series.defaultLabel);
};

const hasResponseField = (list, field) =>
  list.some((item) => item && field in item);

const buildActiveIndexes = (config, forecastList = []) => {
  const list = normalizeListResponse(forecastList);

  return FORECAST_SERIES.filter((series) => {
    if (!hasResponseField(list, series.valKey)) return false;
    if (config[series.statusKey] === false) return false;
    return true;
  }).map((series) => ({
    key: series.configKey,
    valKey: series.valKey,
    label: getSeriesLabel(series, config),
    color: series.color,
  }));
};

const ChartSettingsForm = ({ tempConfig, onConfigChange, onStatusToggle }) => (
  <Grid container spacing={2} sx={{ mt: 1 }}>
    {CONFIG_TEXT_FIELDS.map((field) => (
      <Grid item xs={12} key={field.key}>
        <InputLabel>{field.label}</InputLabel>
        <FormControl fullWidth>
          <TextField
            variant="outlined"
            placeholder={`Enter ${field.label.toLowerCase()}`}
            name={field.key}
            value={tempConfig[field.key] || ''}
            onChange={onConfigChange}
          />
        </FormControl>
      </Grid>
    ))}
    {STATUS_TOGGLE_FIELDS.map((field) => (
      <Grid item xs={12} key={field.key}>
        <Box
          display="flex"
          alignItems="center"
          justifyContent="space-between"
        >
          <InputLabel sx={{ mb: 0 }}>{field.label}</InputLabel>
          <Switch
            checked={!!tempConfig[field.key]}
            onChange={() => onStatusToggle(field.key)}
            color="primary"
          />
        </Box>
      </Grid>
    ))}
  </Grid>
);

// Chart configuration generator
const generateChartOptions = (
  dates,
  forecastList,
  activeIndexes,
  xAxisLabel
) => ({
  chart: {
    type: 'area',
    height: CHART_HEIGHT,
    zoom: { enabled: true },
    toolbar: {
      show: false,
    },
    animations: {
      enabled: true,
      easing: 'easeinout',
      speed: 800,
    },
  },
  dataLabels: {
    enabled: false,
  },
  fill: {
    type: 'gradient',
    gradient: {
      shadeIntensity: 1,
      opacityFrom: 0.4,
      opacityTo: 0.1,
      stops: [0, 90, 100],
    },
  },
  markers: {
    size: 4,
    colors: ['#fff'],
    strokeColors: activeIndexes.map((index) => index.color),
    strokeWidth: 2,
    hover: {
      size: 6,
    },
  },
  grid: {
    row: {
      colors: ['#f3f3f3', 'transparent'],
      opacity: 0.5,
    },
    borderColor: '#e0e0e0',
  },
  stroke: {
    curve: 'smooth',
    width: 2,
  },
  xaxis: {
    categories: dates,
    title: {
      text: 'Date',
      style: {
        fontSize: '14px',
        fontWeight: 600,
      },
    },
    labels: {
      rotate: -45,
      rotateAlways: false,
      hideOverlappingLabels: true,
      style: {
        fontSize: '12px',
      },
    },
  },
  yaxis: {
    title: {
      text: xAxisLabel || 'Metrics Value',
      style: {
        fontSize: '14px',
        fontWeight: 600,
      },
    },
    labels: {
      formatter: (value) => value?.toFixed(2) || '0.00',
    },
  },
  tooltip: {
    shared: true,
    intersect: false,
    custom: function ({ dataPointIndex }) {
      const data = forecastList[dataPointIndex];
      if (!data) return '';

      const rows = activeIndexes
        .map(
          (index) => `
          <div style="margin: 4px 0; color: ${index.color};">
            <span style="font-weight: 500;">${index.label}:</span>
            ${data[index.valKey]?.toFixed(2) || 'N/A'}
          </div>
        `
        )
        .join('');

      return `
        <div style="padding: 12px; font-size: 14px; background: white; border-radius: 4px; box-shadow: 0 2px 8px rgba(0,0,0,0.15);">
          <div style="font-weight: 600; margin-bottom: 8px; color: #333;">${
            data.date
          }</div>
          ${rows}
        </div>
      `;
    },
  },
  legend: {
    show: activeIndexes.length > 0,
    position: 'top',
    horizontalAlign: 'right',
    fontSize: '14px',
    markers: {
      width: 12,
      height: 12,
      radius: 12,
    },
  },
  colors: activeIndexes.map((index) => index.color),
});

const ForecastChart = () => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const { categoryId, subCategoryId } = useParams();

  // Computed values
  const assetId = useMemo(
    () => subCategoryId || categoryId,
    [subCategoryId, categoryId]
  );
  const scope = useMemo(
    () => (subCategoryId ? 'asset' : 'portfolio'),
    [subCategoryId]
  );
  const assetType = useMemo(
    () => (subCategoryId ? 'assets' : 'portfolio'),
    [subCategoryId]
  );

  // State
  const [forecastList, setForecastList] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [openSettings, setOpenSettings] = useState(false);
  const [config, setConfig] = useState({
    ...INITIAL_CONFIG,
    scope,
    assetId,
  });
  const [tempConfig, setTempConfig] = useState({
    ...INITIAL_CONFIG,
    scope,
    assetId,
  });
  const [isSaving, setIsSaving] = useState(false);

  // Fetch forecast data
  const fetchForecastData = useCallback(async () => {
    setIsLoading(true);
    try {
      const payload = {
        assetId,
        limit: DEFAULT_LIMIT,
        skip: DEFAULT_SKIP,
        type: assetType,
      };

      const response = await dispatch(getForeCastGraph(payload));

      if (response?.status === 200 && response?.data) {
        setForecastList(normalizeListResponse(response.data));
      } else {
        setForecastList([]);
      }
    } catch (error) {
      toast.error('Failed to fetch forecast data');
      setForecastList([]);
    } finally {
      setIsLoading(false);
    }
  }, [assetId, assetType, dispatch]);

  // Fetch configuration
  const fetchConfig = useCallback(async () => {
    try {
      const response = await dispatch(
        getLiveForecastConfig({ scope, assetId, context: 'forecasts' })
      );

      if (response?.status === 200 && response?.data) {
        setConfig((prev) => ({
          ...prev,
          ...normalizeConfig(response.data),
          scope,
          assetId,
        }));
      }
    } catch (error) {
      // Silent fail - use default config
    }
  }, [scope, assetId, dispatch]);

  // Initial data fetch
  useEffect(() => {
    fetchForecastData();
    fetchConfig();
  }, [fetchForecastData, fetchConfig]);

  const activeIndexes = useMemo(
    () => buildActiveIndexes(config, forecastList),
    [config, forecastList]
  );

  const chartDates = useMemo(
    () => forecastList.map((item) => item.date || 'N/A'),
    [forecastList]
  );

  // Chart options and series
  const chartOptions = useMemo(
    () =>
      generateChartOptions(
        chartDates,
        forecastList,
        activeIndexes,
        config.y_axis
      ),
    [chartDates, forecastList, activeIndexes, config.y_axis]
  );

  const chartSeries = useMemo(
    () =>
      activeIndexes.map((index) => ({
        name: index.label,
        data: forecastList.map((item) => item[index.valKey] ?? null),
      })),
    [activeIndexes, forecastList]
  );

  // Handlers
  const handleOpenSettings = useCallback(() => {
    setTempConfig(config);
    setOpenSettings(true);
  }, [config]);

  const handleCloseSettings = useCallback(() => {
    setOpenSettings(false);
    setTempConfig(config);
  }, [config]);

  const handleConfigChange = useCallback((e) => {
    const { name, value } = e.target;
    setTempConfig((prev) => ({ ...prev, [name]: value }));
  }, []);

  const handleStatusToggle = useCallback((key) => {
    setTempConfig((prev) => ({ ...prev, [key]: !prev[key] }));
  }, []);

  const handleSaveConfig = useCallback(async () => {
    setIsSaving(true);
    try {
      const payload = {
        ...tempConfig,
        ref_id: tempConfig.ref_id || assetId,
        context: 'forecasts',
        scope,
        assetId,
      };

      const response = await dispatch(createLiveForecastConfig(payload));

      if (response?.status === 200) {
        toast.success('Chart settings saved successfully');
        setConfig(tempConfig);
        setOpenSettings(false);
        await fetchForecastData();
      } else {
        throw new Error('Failed to save settings');
      }
    } catch (error) {
      toast.error(error?.message || 'Failed to save chart settings');
    } finally {
      setIsSaving(false);
    }
  }, [tempConfig, assetId, scope, dispatch, fetchForecastData]);

  // Render loading state
  if (isLoading) {
    return (
      <Box>
        <Stack direction="row" justifyContent="flex-end" gap={2} mb={2}>
          <Skeleton variant="rectangular" width={100} height={40} />
        </Stack>
        <Skeleton variant="rectangular" width="100%" height={CHART_HEIGHT} />
      </Box>
    );
  }

  // Render empty state
  if (!forecastList || forecastList.length === 0) {
    return (
      <Box>
        <Stack direction="row" justifyContent="flex-end" gap={2} mb={2}>
          <AnimateButton>
            <PrimaryButton title="Settings" onClick={handleOpenSettings} />
          </AnimateButton>
        </Stack>
        <Box
          sx={{
            height: CHART_HEIGHT,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            border: '1px dashed #ccc',
            borderRadius: 1,
            backgroundColor: '#f9f9f9',
          }}
        >
          <Typography variant="h6" color="text.secondary">
            No forecast data available
          </Typography>
        </Box>

        {/* Settings Dialog */}
        <Dialog
          open={openSettings}
          onClose={handleCloseSettings}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="center"
            >
              <Typography variant="h6">Chart Settings</Typography>
              <IconButton
                onClick={handleCloseSettings}
                sx={{ color: theme.palette.grey[500] }}
                aria-label="close"
              >
                <CloseIcon />
              </IconButton>
            </Stack>
          </DialogTitle>
          <DialogContent>
            <ChartSettingsForm
              tempConfig={tempConfig}
              onConfigChange={handleConfigChange}
              onStatusToggle={handleStatusToggle}
            />
          </DialogContent>
          <DialogActions>
            <Box display="flex" gap={2} px={2}>
              <AnimateButton>
                <PrimaryButton
                  title="Save"
                  onClick={handleSaveConfig}
                  disabled={isSaving}
                />
              </AnimateButton>
            </Box>
          </DialogActions>
        </Dialog>
      </Box>
    );
  }

  return (
    <Box>
      <Stack direction="row" justifyContent="flex-end" gap={2} mb={2}>
        <AnimateButton>
          <PrimaryButton title="Settings" onClick={handleOpenSettings} />
        </AnimateButton>
      </Stack>

      <Grid container>
        <Grid item xs={12}>
          <Chart
            options={chartOptions}
            series={chartSeries}
            type="area"
            height={CHART_HEIGHT}
          />
        </Grid>
      </Grid>

      {/* Settings Dialog */}
      <Dialog
        open={openSettings}
        onClose={handleCloseSettings}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
          >
            <Typography variant="h6">Chart Settings</Typography>
            <IconButton
              onClick={handleCloseSettings}
              sx={{ color: theme.palette.grey[500] }}
              aria-label="close"
            >
              <CloseIcon />
            </IconButton>
          </Stack>
        </DialogTitle>
        <DialogContent>
          <ChartSettingsForm
            tempConfig={tempConfig}
            onConfigChange={handleConfigChange}
            onStatusToggle={handleStatusToggle}
          />
        </DialogContent>
        <DialogActions>
          <Box display="flex" gap={2} px={2}>
            <AnimateButton>
              <PrimaryButton
                title="Save"
                onClick={handleSaveConfig}
                disabled={isSaving}
              />
            </AnimateButton>
          </Box>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ForecastChart;
